import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { OPTIMIZED_ROUTES } from '../../mocks/routes';

export default function RouteOptimizationPage() {
  const [selectedRoute, setSelectedRoute] = useState(OPTIMIZED_ROUTES[0]);
  const [optimizing, setOptimizing] = useState(false);
  const [optimized, setOptimized] = useState(false);

  const handleOptimize = () => {
    setOptimizing(true);
    setOptimized(false);
    setTimeout(() => { setOptimizing(false); setOptimized(true); }, 2000);
  };

  const mapUrl = `https://www.google.com/maps/embed?pb=!1m28!1m12!1m3!1d387190!2d${selectedRoute.stops[0].lng}!3d${selectedRoute.stops[0].lat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!4m13!3e0!4m5!1s0x89c259a9b3117469%3A0xd134e199a405a163!2s${encodeURIComponent(selectedRoute.stops[0].address + ' ' + selectedRoute.stops[0].city)}!3m2!1d${selectedRoute.stops[0].lat}!2d${selectedRoute.stops[0].lng}!4m5!1s0x89c259af10ea8043%3A0xb37a6c1c3b6dd1b5!2s${encodeURIComponent(selectedRoute.stops[selectedRoute.stops.length - 1].address)}!3m2!1d${selectedRoute.stops[selectedRoute.stops.length - 1].lat}!2d${selectedRoute.stops[selectedRoute.stops.length - 1].lng}!5e0!3m2!1sen!2sus!4v1620000000000`;

  return (
    <div className="min-h-screen bg-[#1a1d29] font-sans">
      <Navbar />
      <main className="pt-16">
        {/* Header */}
        <div className="bg-[#1e2235] border-b border-white/5 px-6 py-8">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="font-heading text-2xl font-bold text-white">Route Optimization</h1>
              <p className="text-gray-400 text-sm mt-1">AI-powered multi-stop delivery route planning</p>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={selectedRoute.id}
                onChange={(e) => setSelectedRoute(OPTIMIZED_ROUTES.find((r) => r.id === e.target.value) ?? OPTIMIZED_ROUTES[0])}
                className="bg-[#1a1d29] border border-white/10 text-gray-300 text-sm rounded-lg px-3 py-2 outline-none cursor-pointer"
              >
                {OPTIMIZED_ROUTES.map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
              <button
                onClick={handleOptimize}
                disabled={optimizing}
                className="px-5 py-2 bg-[#ff6b35] hover:bg-[#ff8c5a] disabled:opacity-50 text-white font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap text-sm"
              >
                {optimizing ? (
                  <><i className="ri-loader-4-line animate-spin mr-2" />Optimizing...</>
                ) : (
                  <><i className="ri-magic-line mr-2" />Optimize Route</>
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-8">
          {optimized && (
            <div className="mb-6 flex items-center gap-3 bg-green-400/10 border border-green-400/20 rounded-xl px-5 py-3">
              <div className="w-5 h-5 flex items-center justify-center text-green-400">
                <i className="ri-checkbox-circle-line" />
              </div>
              <p className="text-green-400 text-sm font-medium">Route optimized! Estimated savings: 42km and 35 minutes.</p>
            </div>
          )}

          {/* Metrics */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Total Stops', value: selectedRoute.stops.length, icon: 'ri-map-pin-line', color: 'text-[#ff6b35]' },
              { label: 'Total Distance', value: `${selectedRoute.totalDistance} km`, icon: 'ri-route-line', color: 'text-blue-400' },
              { label: 'Est. Duration', value: `${Math.floor(selectedRoute.estimatedTime / 60)}h ${selectedRoute.estimatedTime % 60}m`, icon: 'ri-time-line', color: 'text-purple-400' },
              { label: 'Fuel Cost', value: `$${selectedRoute.fuelCost.toFixed(2)}`, icon: 'ri-gas-station-line', color: 'text-green-400' },
            ].map((m) => (
              <div key={m.label} className="bg-[#1e2235] border border-white/5 rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-6 h-6 flex items-center justify-center ${m.color}`}>
                    <i className={`${m.icon} text-lg`} />
                  </div>
                  <span className="text-gray-400 text-xs">{m.label}</span>
                </div>
                <div className={`font-heading font-bold text-2xl ${m.color}`}>{m.value}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Stop list */}
            <div className="lg:col-span-2 bg-[#1e2235] border border-white/5 rounded-2xl overflow-hidden">
              <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
                <h2 className="text-white font-semibold text-sm">Delivery Stops</h2>
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <i className="ri-truck-line" />
                  <span>{selectedRoute.driver} · {selectedRoute.vehicle}</span>
                </div>
              </div>
              <div className="p-4 space-y-3">
                {selectedRoute.stops.map((stop, idx) => (
                  <div key={stop.id} className={`flex items-start gap-3 p-3 rounded-xl border transition-all ${
                    stop.status === 'current' ? 'border-[#ff6b35]/40 bg-[#ff6b35]/5' :
                    stop.status === 'completed' ? 'border-green-400/20 bg-green-400/5' :
                    'border-white/5 bg-[#1a1d29]/50'
                  }`}>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                      stop.status === 'current' ? 'bg-[#ff6b35] text-white' :
                      stop.status === 'completed' ? 'bg-green-400/20 text-green-400' :
                      'bg-white/10 text-gray-500'
                    }`}>
                      {stop.status === 'completed' ? <i className="ri-check-line" /> : idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{stop.address}</p>
                      <p className="text-gray-500 text-xs">{stop.city}</p>
                      <div className="flex items-center gap-3 mt-1.5">
                        <span className="text-gray-400 text-xs"><i className="ri-time-line mr-1" />{stop.timeWindow}</span>
                        <span className="text-gray-400 text-xs"><i className="ri-box-3-line mr-1" />{stop.packages} pkgs</span>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          stop.priority === 'high' ? 'bg-red-400/10 text-red-400' :
                          stop.priority === 'medium' ? 'bg-yellow-400/10 text-yellow-400' :
                          'bg-gray-400/10 text-gray-400'
                        }`}>
                          {stop.priority}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Map */}
            <div className="lg:col-span-3 bg-[#1e2235] border border-white/5 rounded-2xl overflow-hidden">
              <div className="px-5 py-4 border-b border-white/5 flex items-center gap-2">
                <div className="w-6 h-6 flex items-center justify-center text-[#ff6b35]">
                  <i className="ri-map-2-line" />
                </div>
                <span className="text-white font-semibold text-sm">Route Visualization</span>
                {selectedRoute.optimized && (
                  <span className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-400/10 border border-green-400/20 text-green-400 text-xs">
                    <i className="ri-check-line" />
                    Optimized
                  </span>
                )}
              </div>
              <div className="h-[500px]">
                <iframe
                  title="Route Optimization Map"
                  src={mapUrl}
                  className="w-full h-full border-0"
                  loading="lazy"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
